import { useEffect, useState } from 'react';
import { RefreshCw, AlertCircle, X } from 'lucide-react';
import { useAppVersion } from '../hooks/useAppVersion';
import { reloadToLatest } from '../lib/appVersion';

/** How long "Later" quiets the prompt before it comes back. */
const SNOOZE_MS = 30 * 60 * 1000;

/*  ─────────────────────────────────────────────────────────────
    "A new version is available — refresh."

    Mounted once, globally. A tab left open across a deploy keeps running the
    bundle it loaded; nothing in the browser will ever swap that out on its
    own. The minute-by-minute session-info poll notices the server is serving a
    different release and this offers the reload that picks it up.

    Deliberately a prompt and not an automatic reload: someone may be
    mid-reply. "Later" quiets it for half an hour, and any further deploy
    (a hotfix on top of the release) brings it straight back.
    ───────────────────────────────────────────────────────────── */
export default function UpdateAvailableBanner() {
  const { updateAvailable, buildVersion, serverVersion } = useAppVersion();
  const [snoozedFor, setSnoozedFor] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // A snooze covers the version it was taken against, and expires on its own.
  useEffect(() => {
    if (!snoozedFor) return;
    const t = setTimeout(() => setSnoozedFor(null), SNOOZE_MS);
    return () => clearTimeout(t);
  }, [snoozedFor]);

  if (!updateAvailable || snoozedFor === serverVersion) return null;

  const refresh = () => {
    setRefreshing(true);
    void reloadToLatest();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 pointer-events-none"
    >
      {/* Deliberately alarm-coloured: the old brand-blue card read as a tip
          and people scrolled past it while running a stale build. The token is
          per-theme, so this stays legible in the high-contrast and monochrome
          themes too. */}
      <div
        className="pointer-events-auto flex items-start gap-3 rounded-xl shadow-lg w-full"
        style={{
          maxWidth: 520,
          padding: '14px 16px',
          background: 'var(--scpa-surface)',
          border: '2px solid var(--scpa-status-error)',
          borderLeftWidth: 6,
        }}
      >
        <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'var(--scpa-status-error)' }} />

        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold" style={{ color: 'var(--scpa-status-error)' }}>
            ctTicketing {serverVersion ? `v${serverVersion} ` : ''}is available
          </div>
          <div className="text-[13px] text-fg mt-1">
            You&apos;re running v{buildVersion}. Refresh to pick up the update — your
            open work is saved.
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            <button type="button" className="btn btn-primary" onClick={refresh} disabled={refreshing}>
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh now'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setSnoozedFor(serverVersion)}
            >
              Later
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setSnoozedFor(serverVersion)}
          aria-label="Dismiss"
          title="Later"
          style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--scpa-text-muted)' }}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
